import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery } from 'wasp/client/operations';
import { getTenants, deleteTenant, updateTenant } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { withSectionProtection } from '../../client/components/ProtectedSection';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';
import {
  Search,
  Filter,
  Building2,
  Phone, 
  Mail, 
  Globe, 
  MapPin,
  ChevronDown,
  ChevronUp,
  Plus,
  Edit,
  Trash2,
  Eye,
  ToggleLeft,
  ToggleRight,
  Grid3X3,
  List,
  Table
} from 'lucide-react';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { 
  enhancedPageLayoutStyles,
  useResponsiveLayout
} from '../../components/ui/enhanced-page-layout';
import { DetailListItem } from '../../components/ui/detail-list-item';
import { useFixedPageStack } from '../../client/hooks/useFixedPageStack';
import { isAdministrator } from '../../client/lib/platformRole';

interface Tenant {
  id: string;
  companyName: string;
  description: string;
  category: string;
  unitNumber: string | null;
  floor: string | null;
  building: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  contactPerson: string | null;
  businessHours: string | null;
  services: string[];
  status: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'SUSPENDED';
  isVerified: boolean;
  isFeatured: boolean;
  logoUrl: string | null;
  location: {
    id: string;
    name: string;
    locationCode: string | null;
    type: string;
  };
  createdAt: string;
  updatedAt: string;
}

function TenantsPage() {
  const { data: user } = useAuth();
  const showAdminColumns = isAdministrator(user);
  const navigate = useNavigate();
  const params = useParams<{ slug?: string }>();
  const routerLocation = useLocation();
  const { selectedLocation, getLocationIdsForQuery } = useLocationContext();
  const locSlug = resolveLocationSlug(routerLocation.pathname, params.slug, selectedLocation);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('All');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showFilters, setShowFilters] = useState(false);
  // Use responsive layout hook
  const { viewMode, setViewMode, isMobile, shouldCollapseSidebar, hideViewSwitcher } = useResponsiveLayout('table');

  // Fetch tenants using filtered location IDs
  const locationIdsForQuery = getLocationIdsForQuery();
  const { data: allTenants = [], isLoading, error, refetch } = useQuery(getTenants, {
    filters: {
      locationIds: locationIdsForQuery,
    }
  }, {
    enabled: locationIdsForQuery.length > 0
  });

  // Filter tenants client-side (like notices page)
  const filteredTenants = React.useMemo(() => {
    let filtered = allTenants;

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(tenant =>
        tenant.companyName?.toLowerCase().includes(searchLower) ||
        tenant.description?.toLowerCase().includes(searchLower) ||
        tenant.unitNumber?.toLowerCase().includes(searchLower) ||
        tenant.phone?.toLowerCase().includes(searchLower) ||
        tenant.email?.toLowerCase().includes(searchLower) ||
        tenant.category?.toLowerCase().includes(searchLower)
      );
    }

    if (selectedLocationFilter !== 'All') {
      filtered = filtered.filter(tenant => tenant.location?.name === selectedLocationFilter);
    }

    // Apply category filter
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(tenant => tenant.category === selectedCategory);
    }

    // Apply status filter
    if (selectedStatus !== 'All') {
      filtered = filtered.filter(tenant => tenant.status === selectedStatus);
    }

    return filtered;
  }, [allTenants, searchTerm, selectedCategory, selectedStatus]);

  // For header display, use the raw data availability
  const hasLocationData = allTenants.length > 0;
  const fixedStack = useFixedPageStack({
    secondaryKind:
      viewMode === 'table' && !isLoading && !error && hasLocationData ? 'ref' : 'none',
    secondaryRefDeps: [viewMode, showFilters, hasLocationData, isLoading, error],
  });

  // Sort the filtered tenants (now using filteredTenants instead of original tenants)
  const sortedTenants = [...filteredTenants].sort((a, b) => {
    if (sortField === 'companyName') {
      return sortDirection === 'asc' 
        ? a.companyName.localeCompare(b.companyName)
        : b.companyName.localeCompare(a.companyName);
    }
    if (sortField === 'category') {
      return sortDirection === 'asc'
        ? a.category.localeCompare(b.category)
        : b.category.localeCompare(a.category);
    }
    if (sortField === 'status') {
      return sortDirection === 'asc'
        ? a.status.localeCompare(b.status)
        : b.status.localeCompare(a.status);
    }
    return 0;
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDeleteTenant = async (tenantId: string) => {
    if (window.confirm('Are you sure you want to delete this tenant?')) {
      try {
        await deleteTenant({ tenantId });
        refetch(); // Refresh the tenant list
      } catch (error) {
        console.error('Error deleting tenant:', error);
        alert('Failed to delete tenant. Please try again.');
      }
    }
  };

  const handleAddTenant = () => {
    navigate(locationSectionPath(locSlug, 'tenants', 'add'));
  };

  const handleViewTenant = (tenantId: string) => {
    navigate(locationSectionPath(locSlug, 'tenants', tenantId));
  };

  const handleEditTenant = (tenantId: string) => {
    navigate(locationSectionPath(locSlug, 'tenants', tenantId, 'edit'));
  };

  const handleDeactivateTenant = async (tenantId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    const action = newStatus === 'INACTIVE' ? 'deactivate' : 'reactivate';
    
    if (window.confirm(`Are you sure you want to ${action} this tenant?`)) {
      try {
        await updateTenant({ 
          tenantId, 
          data: { status: newStatus as 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'SUSPENDED' }
        });
        refetch(); // Refresh the tenant list
      } catch (error) {
        console.error(`Error ${action}ing tenant:`, error);
        alert(`Failed to ${action} tenant. Please try again.`);
      }
    }
  };


  // Get unique categories and locations for filters
  const categories = Array.from(new Set(allTenants.map((t: any) => t.category).filter(Boolean)));
  const locations = Array.from(new Set(allTenants.map((t: any) => t.location.name)));

  return (
    <DashboardLayout 
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <div className={fixedStack.pageContainerClassName} style={fixedStack.pageContainerStyle}>
        {/* Fixed Header */}
        <div
          ref={fixedStack.pageHeaderRef}
          className="fixed top-[var(--header-height)] left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-white transition-all duration-300"
        >
          <div className="px-4 py-3">
          <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Tenants Directory</h1>
                <p className="text-sm text-gray-600 mt-1">Manage tenant companies at {selectedLocation?.name || 'All Locations'} ({filteredTenants.length} tenants)</p>
              </div>
              <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(f => !f)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  showFilters
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-4 h-4" />
                <span className="hidden lg:inline">Filters</span>
              </button>
              {user && (
                <button
                onClick={handleAddTenant}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden lg:inline">Add Tenant</span>
                </button>
              )}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Table Header - Only for table view */}
        {!isLoading && !error && hasLocationData && viewMode === 'table' && (
          <div
            ref={fixedStack.secondaryStackRef}
            className={`fixed ${fixedStack.secondaryFixedTopClass} left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-sidebar-hover border-b border-border transition-all duration-300`}
          >
            <div className="px-6 py-3 space-y-3">
              {/* Column Headers */}
              <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider select-none cursor-default">
                <div className="col-span-4">Company & Description</div>
                <div className="col-span-1">Category</div>
                <div className="col-span-2">Location</div>
                <div className={showAdminColumns ? 'col-span-3' : 'col-span-5'}>Contact</div>
                {showAdminColumns && <div className="col-span-1">Status</div>}
                {showAdminColumns && <div className="col-span-1">Actions</div>}
              </div>
              {/* Column Filters */}
              {showFilters && <div className="grid grid-cols-12 gap-4">
                {/* Company & Description Filter */}
                <div className="col-span-4">
                  <input
                    type="text"
                    placeholder="All"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {/* Category Filter */}
                <div className="col-span-1">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="All">All</option>
                    {categories.map((category: any) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                {/* Location Filter */}
                <div className="col-span-2">
                  <select
                    value={selectedLocationFilter}
                    onChange={(e) => setSelectedLocationFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="All">All</option>
                    {locations.map((location: any) => (
                      <option key={location} value={location}>{location}</option>
                    ))}
                  </select>
                </div>
                {/* Contact Filter - placeholder */}
                <div className={showAdminColumns ? 'col-span-3' : 'col-span-5'}>
                  <select
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled
                  >
                    <option value="All">All</option>
                  </select>
                </div>
                {showAdminColumns && (
                  <>
                    {/* Status Filter */}
                    <div className="col-span-1">
                      <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="All">All</option>
                        <option value="ACTIVE">Active</option>
                        <option value="INACTIVE">Inactive</option>
                        <option value="PENDING">Pending</option>
                        <option value="SUSPENDED">Suspended</option>
                      </select>
                    </div>
                    {/* Actions - No filter */}
                    <div className="col-span-1">
                      <div className="w-full px-3 py-2"></div>
                    </div>
                  </>
                )}
              </div>}
            </div>
          </div>
        )}

        {/* Scrollable Content */}
        <div
          className={fixedStack.scrollContainerClassName}
          style={fixedStack.scrollContainerStyle}
        >
          <div className="w-full">
        {/* Loading State */}
        {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading tenants...</p>
              </div>
          </div>
        )}

        {/* Error State */}
        {error && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Tenants</h3>
                <p className="text-gray-600">Please try again later.</p>
              </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && sortedTenants.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Tenants Found</h3>
                <p className="text-gray-600">Try adjusting your search or filters to find what you're looking for.</p>
              </div>
          </div>
        )}

        {/* Content */}
        {!isLoading && !error && sortedTenants.length > 0 && (
          <>
            {/* Table View */}
            {viewMode === 'table' && (
              <div className="w-full">
                {/* Table Body - No internal scroll */}
                <div className="divide-y divide-gray-200">
                    {sortedTenants.map((tenant) => (
                      <div
                        key={tenant.id}
                        onClick={() => handleViewTenant(tenant.id)}
                        className="px-6 py-3 hover:bg-hover transition-colors cursor-pointer"
                      >
                        <div className="grid grid-cols-12 gap-4 items-start">
                          {/* Company & Description */}
                          <div className="col-span-4">
                              <div className="text-sm font-medium text-gray-900">{tenant.companyName}</div>
                              <div className="text-sm text-gray-500 line-clamp-2">{tenant.description}</div>
                            </div>
                          
                          {/* Category */}
                          <div className="col-span-1">
                          <span className="text-sm text-gray-600">
                              {tenant.category}
                            </span>
                          </div>
                          
                          {/* Location */}
                          <div className="col-span-2">
                            <div className="text-sm">
                              <div className="text-gray-900 font-medium">
                                {tenant.floor && `${tenant.floor} Level`}
                              </div>
                              <div className="text-gray-600">
                                {tenant.unitNumber && `Unit ${tenant.unitNumber}`}
                              </div>
                            </div>
                          </div>
                          
                          {/* Contact */}
                          <div className={showAdminColumns ? 'col-span-3' : 'col-span-5'}>
                            <div className="space-y-1">
                              {tenant.phone && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Phone className="w-4 h-4 text-gray-400" />
                                  <span className="truncate">{tenant.phone}</span>
                                </div>
                              )}
                              {tenant.email && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Mail className="w-4 h-4 text-gray-400" />
                                  <span className="truncate">{tenant.email}</span>
                                </div>
                              )}
                              {tenant.website && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Globe className="w-4 h-4 text-gray-400" />
                                  <span className="truncate">{tenant.website}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {showAdminColumns && (
                            <>
                              {/* Status */}
                              <div className="col-span-1">
                                <span className="text-sm text-gray-600">
                                  {tenant.status.toLowerCase()}
                                </span>
                              </div>
                              
                              {/* Actions */}
                              <div className="col-span-1">
                                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    onClick={() => handleViewTenant(tenant.id)}
                                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                    title="View details"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  {user && (
                                    <>
                                      <button
                                        onClick={() => handleEditTenant(tenant.id)}
                                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                        title="Edit tenant"
                                      >
                                        <Edit className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDeactivateTenant(tenant.id, tenant.status)}
                                        className={`p-1 transition-colors ${
                                          tenant.status === 'ACTIVE'
                                            ? 'text-green-500 hover:text-green-700'
                                            : 'text-gray-400 hover:text-green-600'
                                        }`}
                                        title={tenant.status === 'ACTIVE' ? 'Deactivate tenant' : 'Reactivate tenant'}
                                      >
                                        {tenant.status === 'ACTIVE' ? (
                                          <ToggleRight className="w-4 h-4" />
                                        ) : (
                                          <ToggleLeft className="w-4 h-4" />
                                        )}
                                      </button>
                                      <button
                                        onClick={() => handleDeleteTenant(tenant.id)}
                                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                        title="Delete tenant"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      ))}
                </div>
              </div>
            )}

            {/* Cards View */}
            {viewMode === 'cards' && (
              <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {sortedTenants.map((tenant) => (
                    <div
                      key={tenant.id}
                      onClick={() => handleViewTenant(tenant.id)}
                      className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-blue-600" />
                          <span className="text-sm font-medium text-gray-600">
                            {tenant.category}
                          </span>
                        </div>
                        
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${
                          tenant.status === 'ACTIVE' 
                            ? 'text-green-600 bg-green-50 border-green-200' 
                            : tenant.status === 'INACTIVE'
                            ? 'text-red-600 bg-red-50 border-red-200'
                            : tenant.status === 'PENDING'
                            ? 'text-yellow-600 bg-yellow-50 border-yellow-200'
                            : 'text-gray-600 bg-gray-50 border-gray-200'
                        }`}>
                          {tenant.status.toLowerCase()}
                        </span>
                      </div>
                      
                      <h3 className="font-semibold text-gray-900 text-lg mb-2">
                        {tenant.companyName}
                      </h3>
                      
                      {tenant.description && (
                        <p className="text-gray-600 mb-4 line-clamp-3">
                        {tenant.description}
                      </p>
                      )}
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="w-4 h-4" />
                          <span>
                            {tenant.floor && `${tenant.floor} Level`}
                            {tenant.unitNumber && `, Unit ${tenant.unitNumber}`}
                          </span>
                        </div>
                        {tenant.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="w-4 h-4" />
                            <span>{tenant.phone}</span>
                          </div>
                        )}
                        {tenant.email && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Mail className="w-4 h-4" />
                            <span>{tenant.email}</span>
                          </div>
                        )}
                        {tenant.website && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Globe className="w-4 h-4" />
                            <span>{tenant.website}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <span>Created: {new Date(tenant.createdAt).toLocaleDateString()}</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewTenant(tenant.id)}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {user && (
                            <>
                              <button
                                onClick={() => handleEditTenant(tenant.id)}
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                title="Edit tenant"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeactivateTenant(tenant.id, tenant.status)}
                                className={`p-1 transition-colors ${
                                  tenant.status === 'ACTIVE' 
                                    ? 'text-green-500 hover:text-green-700' 
                                    : 'text-gray-400 hover:text-green-600'
                                }`}
                                title={tenant.status === 'ACTIVE' ? 'Deactivate tenant' : 'Reactivate tenant'}
                              >
                                {tenant.status === 'ACTIVE' ? (
                                  <ToggleRight className="w-4 h-4" />
                                ) : (
                                  <ToggleLeft className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => handleDeleteTenant(tenant.id)}
                                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                title="Delete tenant"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  </div>
              </div>
            )}

            {/* Detail List View */}
            {viewMode === 'list' && (
              <div className="pb-2">
                {sortedTenants.map((tenant) => {
                  const locationParts = [
                    tenant.floor && `${tenant.floor} Level`,
                    tenant.unitNumber && `Unit ${tenant.unitNumber}`,
                  ].filter(Boolean);
                  return (
                    <DetailListItem
                      key={tenant.id}
                      title={tenant.companyName}
                      description={tenant.description}
                      website={tenant.website}
                      onSelect={() => handleViewTenant(tenant.id)}
                      meta={
                        <>
                          {tenant.category && <span>{tenant.category}</span>}
                          {locationParts.length > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="w-3 h-3 flex-shrink-0" />
                              {locationParts.join(', ')}
                            </span>
                          )}
                          <span
                            className={`px-2 py-0.5 rounded-full border ${
                              tenant.status === 'ACTIVE'
                                ? 'text-green-600 bg-green-50 border-green-200'
                                : tenant.status === 'INACTIVE'
                                  ? 'text-red-600 bg-red-50 border-red-200'
                                  : tenant.status === 'PENDING'
                                    ? 'text-yellow-600 bg-yellow-50 border-yellow-200'
                                    : 'text-gray-600 bg-gray-50 border-gray-200'
                            }`}
                          >
                            {tenant.status.toLowerCase()}
                          </span>
                        </>
                      }
                      trailing={
                        <>
                          <button
                            onClick={() => handleViewTenant(tenant.id)}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            title="View details"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          {user && (
                            <>
                              <button
                                onClick={() => handleEditTenant(tenant.id)}
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                title="Edit tenant"
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeactivateTenant(tenant.id, tenant.status)}
                                className={`p-1 transition-colors ${
                                  tenant.status === 'ACTIVE'
                                    ? 'text-green-500 hover:text-green-700'
                                    : 'text-gray-400 hover:text-green-600'
                                }`}
                                title={tenant.status === 'ACTIVE' ? 'Deactivate tenant' : 'Reactivate tenant'}
                              >
                                {tenant.status === 'ACTIVE' ? (
                                  <ToggleRight className="w-5 h-5" />
                                ) : (
                                  <ToggleLeft className="w-5 h-5" />
                                )}
                              </button>
                              <button
                                onClick={() => handleDeleteTenant(tenant.id)}
                                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                title="Delete tenant"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </>
                          )}
                        </>
                      }
                    />
                  );
                })}
              </div>
            )}
          </>
        )}
        </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default withSectionProtection(TenantsPage, 'tenants');



